import { Component, OnInit, signal, WritableSignal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CardModule } from 'primeng/card';
import { TableModule } from 'primeng/table';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { RecruitingService } from 'src/app/api/recruiting.service';
import { CallAllTimeLeaderboardResponse, CallLeaderboardResponse } from 'src/app/api/types/recruiting';
import { UiModule } from 'src/app/ui/ui.module';
import { DateTime } from 'luxon';
import { DatePipe } from '@angular/common';
import { Column, TableHeaderComponent, TableColumnComponent } from 'src/app/ui/tables';
import { MessageService } from 'primeng/api';
import { ErrorService } from 'src/app/services/error.service';
import { IconButtonComponent } from 'src/app/ui/icon-button/icon-button.component';
import { IconService } from 'src/app/ui/icon.service';
import { LocalAuthService } from 'src/app/auth/local-auth.service';

interface LeaderboardTableData {
  rank: number;
  staffName: string;
  id?: number;
  calls: number;
  meetings: number;
  total: number;
  week?: Date;
  month?: string;
  year?: number;
  recordYear?: string;
  weekDate?: string;
  dateString?: string;
}

@Component({
  selector: 'app-call-leaderboard',
  templateUrl: './call-leaderboard.component.html',
  styleUrls: ['./call-leaderboard.component.scss'],
  standalone: true,
  imports: [CommonModule, UiModule, TableColumnComponent, TableHeaderComponent, IconButtonComponent],
  providers: [DatePipe]
})
export class CallLeaderboardComponent implements OnInit {
  loading = signal(false);
  error = signal<string | null>(null);

  allTimeLoading = signal(false);
  allTimeError = signal<string | null>(null);

  leaderboardData: CallLeaderboardResponse | null = null;
  allTimeLeaderboardData: CallAllTimeLeaderboardResponse | null = null;
  
  // Main tables data
  weekTableData: LeaderboardTableData[] = [];
  monthTableData: LeaderboardTableData[] = [];
  yearTableData: LeaderboardTableData[] = [];
  
  // Dialog tables data
  allTimeCallWeekData: LeaderboardTableData[] = [];
  allTimeCallMonthData: LeaderboardTableData[] = [];
  allTimeCallYearData: LeaderboardTableData[] = [];
  
  // Dialog visibility
  showAllTimeDialog = false;
  currentDialogTable: 'Week' | 'Month' | 'Year' = 'Week';
  
  // Expand dialog visibility
  showExpandDialog = false;

  // Column definitions for for-loops
  weekMonthYearColumns: WritableSignal<Column<LeaderboardTableData>[]> = signal([
    { field: 'rank', header: '#', type: 'number', sortable: false, filterable: false },
    { field: 'staffName', header: 'Staff', type: 'text', sortable: false, filterable: false },
    { field: 'calls', header: 'Calls', type: 'number', sortable: false, filterable: false },
    { field: 'meetings', header: 'Meetings', type: 'number', sortable: false, filterable: false },
    { field: 'total', header: 'Total', type: 'number', sortable: false, filterable: false }
  ]);

  dialogColumns: WritableSignal<Column<LeaderboardTableData>[]> = signal([
    { field: 'rank', header: '#', type: 'number', sortable: false, filterable: false },
    { field: 'staffName', header: 'Staff', type: 'text', sortable: false, filterable: false },
    { field: 'dateString', header: 'Date', type: 'text', sortable: false, filterable: false },
    { field: 'total', header: 'Total', type: 'number', sortable: false, filterable: false }
  ]);

  constructor(
    private recruitingService: RecruitingService,
    private datePipe: DatePipe,
    private messageService: MessageService,
    private errorService: ErrorService,
    public icons: IconService,
    private auth: LocalAuthService
  ) {}

  

  private loadLeaderboardData(): void {
    this.loading.set(true);
    this.recruitingService.getCallLeaderboard().subscribe({
      next: (data: CallLeaderboardResponse) => {
        this.leaderboardData = data;
        this.processTableData();
        this.loading.set(false);
      },
      error: (error: any) => {
        this.error.set('Failed to load leaderboard data');
        this.loading.set(false);
        console.error('Error loading leaderboard data:', error);
      }
    });
  }

  private loadAllTimeLeaderboardData(): void {
    this.allTimeLoading.set(true);
    this.recruitingService.getCallAllTimeLeaderboard().subscribe({
      next: (data: CallAllTimeLeaderboardResponse) => {
        this.allTimeLeaderboardData = data;
        this.processAllTimeTableData();
        this.allTimeLoading.set(false);
      }
    });
  }

  private processTableData(): void {
    if (!this.leaderboardData) return;

    this.weekTableData = this.transformTableData(this.leaderboardData.week, 'week');
    this.monthTableData = this.transformTableData(this.leaderboardData.month, 'month');
    this.yearTableData = this.transformTableData(this.leaderboardData.year, 'year');
   
  }

  private processAllTimeTableData(): void {
    if (!this.allTimeLeaderboardData) return;

    this.allTimeCallWeekData = this.transformTableData(this.allTimeLeaderboardData.allTimeWeek, 'week');
    this.allTimeCallMonthData = this.transformTableData(this.allTimeLeaderboardData.allTimeMonth, 'month');
    this.allTimeCallYearData = this.transformTableData(this.allTimeLeaderboardData.allTimeYear, 'year');
  }

  private transformTableData(data: any[], dateType: 'week' | 'month' | 'year'): LeaderboardTableData[] {
    // Sort by total (descending)
    const sortedData = [...data].sort((a, b) => {
      const totalA = a.total || a.calls + (a.meetings || 0);
      const totalB = b.total || b.calls + (b.meetings || 0);
      return totalB - totalA;
    });

    // First map to LeaderboardTableData, then set dateString
    return sortedData.map((item, index) => {
      const mapped: LeaderboardTableData = {
        rank: index + 1,
        staffName: item.staffName || 'Unknown',
        id: item.id,
        calls: item.calls,
        meetings: item.meetings || 0,
        total: item.total || item.calls + (item.meetings || 0),
        week: item.weekDate ? new Date(item.weekDate.toString()) : undefined,
        month: item.month ? item.month : undefined,
        year: item.year ? item.year : undefined,
        recordYear: item.recordyear ? item.recordyear : undefined,
        weekDate: item.weekDate ? item.weekDate : undefined
      };
      return {
        ...mapped,
        dateString: this.setDateString(dateType, mapped)
      };
    });
  }

  setDateString(dateType: 'week' | 'month' | 'year', item: LeaderboardTableData): string {
    if (dateType === 'week') {
      return item.weekDate ? this.datePipe.transform(new Date(item.weekDate.toString()), 'MMM d, yyyy') || '' : '';
    } else if (dateType === 'month') {
      return item.month || '';
    } else if (dateType === 'year') {
      return item.recordYear || '';
    } else {
      return '';
    }
  }

  showAllTimeTable(type: 'Week' | 'Month' | 'Year'): void {
    this.currentDialogTable = type;
    this.showAllTimeDialog = true;
  }

  openExpandDialog(): void {
    this.showExpandDialog = true;
  }

  isCurrentUser(staffId?: number): boolean {
    const current = this.auth.currentUser;
    return !!staffId && !!current && staffId === current.newstaffId;
  }

  ngOnInit(): void {
    this.loadLeaderboardData();
    this.loadAllTimeLeaderboardData();
  }

  exportTableCSV(tableType: 'week' | 'month' | 'year'): void {
    let data: LeaderboardTableData[];
    switch (tableType) {
      case 'week':
        data = this.weekTableData;
        break;
      case 'month':
        data = this.monthTableData;
        break;
      case 'year':
        data = this.yearTableData;
        break;
    }

    // Create CSV content
    const headers = ['Rank', 'Staff', 'Calls', 'Meetings', 'Total'];
    const csvContent = [
      headers.join(','),
      ...data.map(item => [
        item.rank,
        `"${item.staffName}"`,
        item.calls,
        item.meetings,
        item.total
      ].join(','))
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `call-leaderboard-${tableType}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
} 